"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams } from "next/navigation";
import { type RefObject, useEffect, useState } from "react";
import { withDebounce } from "@/utilities/debounce/with-debounce";
const debounceDelay = 15;
const fullHeightPercent = 100;
const roundToNearestDefault = 0.1;
type UseScrollPointsReturn = {
  readonly percentageViewed: number;
};
type UseElementViewedPercentageProps = {
  readonly ref: RefObject<HTMLElement | null>;
  readonly roundToNearest?: number;
};
export function useElementViewedPercentage(t0) {
  const $ = _c(11);
  const {
    ref,
    roundToNearest: t1
  } = t0;
  const roundToNearest = t1 === undefined ? roundToNearestDefault : t1;
  const [percentageViewed, setPercentageViewed] = useState(0);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setPercentageViewed(0);
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== pathname || $[2] !== searchParams) {
    t3 = [pathname, searchParams];
    $[1] = pathname;
    $[2] = searchParams;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== percentageViewed || $[5] !== ref || $[6] !== roundToNearest) {
    t4 = () => {
      const debounceUpdatePercentageViewed = withDebounce(updatePercentageViewed, debounceDelay);
      updatePercentageViewed();
      window.addEventListener("scroll", debounceUpdatePercentageViewed, {
        passive: true
      });
      function updatePercentageViewed() {
        if (ref.current === null) {
          return;
        }
        const currentScrollPercentage = getCurrentScrollPercentage();
        if (percentageViewed < currentScrollPercentage && currentScrollPercentage > 0) {
          setPercentageViewed(currentScrollPercentage);
        }
        if (currentScrollPercentage > fullHeightPercent) {
          setPercentageViewed(fullHeightPercent);
        }
      }
      function getCurrentScrollPercentage() {
        if (ref.current === null) {
          return 0;
        }
        const elementHeight = ref.current.offsetHeight;
        const elementRect = ref.current.getBoundingClientRect();
        const percentage = (window.screen.height - elementRect.top) / elementHeight * fullHeightPercent;
        const roundedPercentage = Math.round(percentage / roundToNearest) * roundToNearest;
        return roundedPercentage;
      }
      return function cleanUp() {
        window.removeEventListener("scroll", debounceUpdatePercentageViewed);
      };
    };
    t5 = [ref, percentageViewed, roundToNearest];
    $[4] = percentageViewed;
    $[5] = ref;
    $[6] = roundToNearest;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== percentageViewed) {
    t6 = {
      percentageViewed
    };
    $[9] = percentageViewed;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}